import React from "react";
import Layout from "@/components/layout/Layout";
import { Code2 } from "lucide-react";

const Endpoint = ({ method, path, desc, sample }) => (
  <div className="ks-card mb-4" data-testid={`api-${method}-${path.replace(/[^\w]/g, "_")}`}>
    <div className="p-4 flex items-start gap-3 border-b border-[var(--ks-border)]">
      <span className={`ks-badge ${method === "GET" ? "ks-badge-success" : "ks-badge-pending"}`}>{method}</span>
      <div className="min-w-0">
        <div className="font-mono text-sm">{path}</div>
        <div className="text-xs text-[var(--ks-text-soft)] mt-1">{desc}</div>
      </div>
    </div>
    {sample && (
      <pre className="text-xs bg-[var(--ks-surface)] p-3 overflow-x-auto font-mono">{sample}</pre>
    )}
  </div>
);

export default function ApiDocs() {
  const base = process.env.REACT_APP_BACKEND_URL + "/api";
  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <Code2 className="text-[var(--ks-primary)]" />
        <div>
          <h1 className="text-xl font-semibold" data-testid="api-docs-title">Etherscan Public API</h1>
          <p className="text-sm text-[var(--ks-text-soft)]">Base URL: <span className="mono">{base}</span></p>
        </div>
      </div>

      <div className="ks-card p-4 mb-6">
        <div className="text-sm">
          <strong>Authentication:</strong> Public endpoints require no auth. Admin endpoints require a Bearer token from <span className="mono">POST /auth/login</span>.
        </div>
      </div>

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-4">Network Stats</h2>
      <Endpoint method="GET" path="/api/stats" desc="Network overview: ETH price (live), gas, tx counts, latest block."
        sample={`curl ${base}/stats`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Blocks</h2>
      <Endpoint method="GET" path="/api/blocks?limit=25&skip=0" desc="Paginated list of blocks (newest first)."
        sample={`curl "${base}/blocks?limit=10"`} />
      <Endpoint method="GET" path="/api/blocks/{number}" desc="Block detail incl. transactions."
        sample={`curl ${base}/blocks/120`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Transactions</h2>
      <Endpoint method="GET" path="/api/transactions?limit=25&skip=0" desc="Paginated list of transactions."
        sample={`curl "${base}/transactions?limit=10"`} />
      <Endpoint method="GET" path="/api/transactions/{hash}" desc="Transaction detail + confirmations."
        sample={`curl ${base}/transactions/0x...`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Addresses</h2>
      <Endpoint method="GET" path="/api/address/{address}" desc="Balance, tokens, and recent transactions."
        sample={`curl ${base}/address/0x1111...`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Tokens</h2>
      <Endpoint method="GET" path="/api/tokens" desc="List all supported ERC-20 tokens with live prices."
        sample={`curl ${base}/tokens`} />
      <Endpoint method="GET" path="/api/tokens/{contract}" desc="Token detail: holders, transfers."
        sample={`curl ${base}/tokens/0x...`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Search</h2>
      <Endpoint method="GET" path="/api/search?q=..." desc="Universal search: block number, block/tx hash, address."
        sample={`curl "${base}/search?q=42"`} />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ks-text-soft)] mb-2 mt-6">Auth &amp; Admin</h2>
      <Endpoint method="POST" path="/api/auth/login" desc='{"email":"...", "password":"..."} → returns { token, user }' />
      <Endpoint method="GET" path="/api/auth/me" desc="Verify current admin (Bearer token required)." />
      <Endpoint method="GET" path="/api/admin/stats" desc="Admin dashboard stats. Bearer token required." />
      <Endpoint method="GET" path="/api/admin/transactions?status=&address=&hash=&date=" desc="Filtered transactions for admin." />
      <Endpoint method="PATCH" path="/api/admin/transactions/{hash}/status" desc='{"status":"Success|Pending|Failed"}' />
      <Endpoint method="POST" path="/api/admin/wallets/mint" desc='{"address":"0x...", "asset":"ETH|KDR|USDT|USDC|LINK|DAI", "amount": 30}' />
      <Endpoint method="GET" path="/api/admin/logs" desc="Audit trail of all admin actions." />
    </Layout>
  );
}
